import { useCallback, useMemo, useState } from 'react';

import type { UUID } from '@aline/common';
import { getStartingPosition, sortPlaybookRulePositionsByRank } from '@aline/common';
import { useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import {
  SelectiveMouseSensor,
  SelectivePointerSensor,
} from 'src/components/workflow/board/utils/keyboardCoordinateGetter';
import {
  useAddPlaybookRulePosition,
  useDeletePlaybookRulePosition,
  useMovePlaybookRulePositions,
  useUpdatePlaybookRulePosition,
} from 'src/hooks';

import type { PlaybookDetailRule } from './types';

/** How far a pointer travels before a press on the ordinal becomes a drag. */
const DRAG_ACTIVATION_DISTANCE_PX = 8;

export interface PositionCommitProps {
  aiGuidance?: string | null;
  ruleText?: string;
}

/**
 * Every state transition a rule's positions can go through: edit, add, delete,
 * reorder — plus the ordering the panel numbers them by.
 *
 * Kept apart from the rendering so which position a drag lands after, and what
 * that sends, can be read and tested without a panel around it. There is no
 * local copy of the list: each of these is a mutation, and the rendered order
 * comes from the ranks the server returns.
 */
export function useRulePositions(rule: PlaybookDetailRule) {
  const { updatePlaybookRulePosition } = useUpdatePlaybookRulePosition();
  const { addPlaybookRulePosition } = useAddPlaybookRulePosition();
  const { deletePlaybookRulePosition } = useDeletePlaybookRulePosition();
  const { movePlaybookRulePositions } = useMovePlaybookRulePositions();

  /**
   * Sorted here so the ordinal labelling in the panel — index 0 the Starting
   * Position, the rest Fallback 1..n — is a property of this list rather than a
   * bet on the order the server happened to send it in.
   */
  const positions = useMemo(
    () => sortPlaybookRulePositionsByRank(rule.positions),
    [rule.positions]
  );

  /**
   * A rule straight out of "Add rule": no title and a single blank position.
   * The panel autofocuses the title for these so the user can start typing.
   */
  const isEmptyRule =
    !rule.title.trim() && positions.length === 1 && !getStartingPosition(positions).ruleText.trim();

  const sensors = useSensors(
    useSensor(SelectivePointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
    }),
    useSensor(SelectiveMouseSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
    })
  );

  const commitPosition = useCallback(
    (positionId: UUID, props: PositionCommitProps): void => {
      void updatePlaybookRulePosition({ positionId, props, ruleId: rule.id });
    },
    [rule.id, updatePlaybookRulePosition]
  );

  // The position just added, so its card can take focus when it renders.
  const [addedPositionId, setAddedPositionId] = useState<UUID | null>(null);

  const addFallback = useCallback(async (): Promise<void> => {
    const { data } = await addPlaybookRulePosition({ props: { ruleText: '' }, ruleId: rule.id });
    // The mutation returns the rule with the new position appended last.
    const added = data?.addPlaybookRulePosition.positions.at(-1);
    if (added) setAddedPositionId(added.id);
  }, [addPlaybookRulePosition, rule.id]);

  const deletePosition = useCallback(
    (positionId: UUID): void => {
      void deletePlaybookRulePosition({ positionId, ruleId: rule.id });
    },
    [deletePlaybookRulePosition, rule.id]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent): void => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const positionIds = positions.map(position => position.id);
      const draggedId = String(active.id);
      const oldIndex = positionIds.indexOf(draggedId);
      const newIndex = positionIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;

      /**
       * The whole list is reordered locally only to read off where the dragged
       * position landed; what gets sent is that one position and the one it now
       * follows. Sending the full order instead would restate positions this
       * drag never touched, overwriting a concurrent edit to them.
       */
      const landedIds = arrayMove(positionIds, oldIndex, newIndex);
      const landedIndex = landedIds.indexOf(draggedId);
      void movePlaybookRulePositions({
        afterPositionId: landedIndex === 0 ? null : landedIds[landedIndex - 1],
        positionIds: [draggedId],
        ruleId: rule.id,
      });
    },
    [movePlaybookRulePositions, positions, rule.id]
  );

  return {
    addFallback,
    addedPositionId,
    commitPosition,
    deletePosition,
    handleDragEnd,
    isEmptyRule,
    positions,
    sensors,
  };
}
